import { useEffect, useState } from "react";
import { Download, X, Smartphone, Share2, PlusSquare, Tv } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

/**
 * InstallPWA — a smart install prompt that adapts to the platform.
 *
 *   Chrome/Edge/Samsung Internet/Android/LG WebOS/Samsung Tizen browsers
 *     fire the standardised `beforeinstallprompt` event. We capture it and
 *     surface a custom, on-brand "Install TimeCapsule" pill.
 *
 *   iOS Safari (iPhone / iPad) does NOT fire the event but is our biggest
 *     PWA installer. We detect iOS + Safari + not-in-standalone and show a
 *     one-time instructions card explaining Share sheet → Add to Home Screen.
 *
 *   Anything else → renders nothing.
 *
 * Dismissal is remembered in localStorage under `tc:pwa-installed-hint`
 * for 30 days so we don't nag returning users.
 */
const STORE_KEY = "tc:pwa-installed-hint";
const HIDE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function shouldSuppress() {
  try {
    const ts = parseInt(localStorage.getItem(STORE_KEY) || "0", 10);
    return ts && Date.now() - ts < HIDE_MS;
  } catch { return false; }
}

function markDismissed() {
  try { localStorage.setItem(STORE_KEY, String(Date.now())); } catch { /* ignore */ }
}

function isStandalone() {
  return (
    (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches) ||
    (typeof navigator !== "undefined" && navigator.standalone === true)
  );
}

function detectPlatform() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && "ontouchend" in document);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  const isSamsungTV = /Tizen/.test(ua);
  const isLGTV = /Web0S|webOS/.test(ua);
  const isTV = isSamsungTV || isLGTV || /SmartTV|SMART-TV|GoogleTV|BRAVIA|Roku/i.test(ua);
  return { isIOS, isSafari, isSamsungTV, isLGTV, isTV };
}

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform] = useState(() => detectPlatform());

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone() || shouldSuppress()) return;

    // Standard install event (Chrome, Edge, Android, Samsung Internet, WebOS, Tizen)
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
      trackEvent("pwa_install_available");
    };

    // Fires after user accepts the browser's install dialog
    const onAppInstalled = () => {
      setVisible(false);
      setDeferred(null);
      trackEvent("pwa_installed");
      markDismissed();
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    // iOS Safari fallback — never fires beforeinstallprompt, so we surface an
    // instructions card after a short delay (only if the user hasn't dismissed).
    let iosTimer;
    if (platform.isIOS && platform.isSafari && !isStandalone()) {
      iosTimer = setTimeout(() => setVisible(true), 4500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [platform.isIOS, platform.isSafari]);

  const dismiss = () => {
    setVisible(false);
    markDismissed();
    trackEvent("pwa_install_dismissed");
  };

  const install = async () => {
    if (!deferred) return;
    trackEvent("pwa_install_click");
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      trackEvent("pwa_install_outcome", { outcome });
      if (outcome === "accepted") markDismissed();
    } catch { /* ignore */ }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  // iOS Safari — no automatic prompt; explain the Share → Add to Home Screen flow
  if (platform.isIOS && platform.isSafari && !deferred) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:bottom-6 sm:right-6 sm:w-[380px] z-[9700] bg-paper text-ink border-2 border-ink shadow-[8px_8px_0_#1A1C20]"
        data-testid="pwa-install-ios"
      >
        <div className="flex items-center justify-between bg-paper-alt border-b-2 border-ink px-3 py-2">
          <div className="flex items-center gap-2 font-mono uppercase tracking-[0.25em] text-[10px]">
            <Smartphone size={12} className="text-press-red" /> Add to Home Screen
          </div>
          <button
            onClick={dismiss}
            className="w-7 h-7 border border-ink flex items-center justify-center hover:bg-ink hover:text-paper"
            data-testid="pwa-install-dismiss"
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        </div>
        <div className="p-4">
          <div className="font-headline uppercase font-black text-lg leading-tight tracking-tight">
            Install TimeCapsule
          </div>
          <p className="mt-1 font-body italic text-sm text-ink-muted">
            Get instant, full-screen access — perfect for the couch or iPad.
          </p>
          <ol className="mt-3 space-y-2 font-mono text-[11px] uppercase tracking-widest">
            <li className="flex items-center gap-2">
              <span className="inline-flex w-5 h-5 items-center justify-center border-2 border-ink font-headline text-[10px]">1</span>
              Tap <Share2 size={12} className="mx-1 inline align-text-bottom" /> Share
            </li>
            <li className="flex items-center gap-2">
              <span className="inline-flex w-5 h-5 items-center justify-center border-2 border-ink font-headline text-[10px]">2</span>
              Choose <PlusSquare size={12} className="mx-1 inline align-text-bottom" /> Add to Home Screen
            </li>
            <li className="flex items-center gap-2">
              <span className="inline-flex w-5 h-5 items-center justify-center border-2 border-ink font-headline text-[10px]">3</span>
              Tap <span className="font-bold">Add</span> — you&rsquo;re done.
            </li>
          </ol>
        </div>
      </div>
    );
  }

  // Standard prompt (Chrome/Edge/Samsung Internet/WebOS/Tizen/Android)
  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:bottom-6 sm:right-6 sm:w-[400px] z-[9700] bg-ink text-paper border-2 border-ink shadow-[8px_8px_0_#BA3F1D]"
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-center justify-between border-b-2 border-paper/30 px-3 py-2">
        <div className="flex items-center gap-2 font-mono uppercase tracking-[0.25em] text-[10px] text-paper/80">
          {platform.isTV ? <Tv size={12} /> : <Download size={12} />}
          {platform.isTV ? "Install on TV" : "Install app"}
        </div>
        <button
          onClick={dismiss}
          className="w-7 h-7 border border-paper/40 flex items-center justify-center hover:bg-paper hover:text-ink"
          data-testid="pwa-install-dismiss"
          aria-label="Dismiss"
        >
          <X size={12} />
        </button>
      </div>
      <div className="p-4">
        <div className="font-headline uppercase font-black text-lg leading-tight tracking-tight">
          {platform.isTV ? "Add TimeCapsule to this TV" : "Install TimeCapsule 24"}
        </div>
        <p className="mt-1 font-body italic text-sm text-paper/70">
          {platform.isTV
            ? "One tap. Launches full-screen. Show it to guests without a URL bar."
            : "One tap install — launches full-screen, works offline, feels native."}
        </p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={install}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-press-red text-paper border-2 border-paper px-3 py-2.5 font-mono uppercase tracking-widest text-xs hover:bg-paper hover:text-ink"
            data-testid="pwa-install-accept"
          >
            <Download size={14} /> {platform.isTV ? "Install on TV" : "Install"}
          </button>
          <button
            onClick={dismiss}
            className="inline-flex items-center justify-center gap-1 border-2 border-paper/40 px-3 py-2.5 font-mono uppercase tracking-widest text-xs hover:bg-paper hover:text-ink"
            data-testid="pwa-install-later"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
